<!-- Image Usage Modal -->
<div *ngIf="isOpen" class="modal-overlay" (click)="closeModal()">
  <div class="modal-content" (click)="$event.stopPropagation()">
    <!-- Modal Header -->
    <div class="modal-header-fixed">
      <div>
        <h2 class="modal-title">{{ translations.imageUsage || 'Image Usage' }}</h2>
        <p class="modal-subtitle">{{ translations.trackImageProcessingUsage || 'Track your image processing usage' }}</p>
      </div>
      <button (click)="closeModal()" class="close-button">
        <svg
          class="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M6 18L18 6M6 6l12 12"
          ></path>
        </svg>
      </button>
    </div>

    <!-- Modal Content -->
    <div class="modal-body">
      <!-- Current Usage Stats -->
      <div *ngIf="currentStats">
        <h3 class="text-lg font-medium text-gray-900 mb-4">{{ translations.currentUsage || 'Current Usage' }}</h3>

        <!-- Usage Overview -->
        <div class="bg-gray-50 rounded-lg p-4 mb-4">
          <div class="flex items-center justify-between mb-2">
            <div class="flex items-baseline gap-2">
              <span class="text-sm font-medium text-gray-700"
                >{{ translations.imagesUsage || 'Images Usage' }}</span
              >
              <span class="text-sm text-gray-600">
                {{ getImagePercentage().toFixed(0) }}%
              </span>
            </div>
            <!-- Toggle Icon -->
            <button
              type="button"
              (click)="toggleView()"
              class="flex items-center justify-center w-10 h-6 bg-gray-200 rounded-full transition-colors duration-200 hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              [class.bg-blue-500]="showRemainingView"
              [class.bg-gray-200]="!showRemainingView"
              [title]="showRemainingView
                  ? (translations.switchToTotalView || 'Switch to Total view')
                  : (translations.switchToRemainingView || 'Switch to Remaining view')"
            >
              <div
                class="w-4 h-4 bg-white rounded-full shadow transform transition-transform duration-200"
                [class.translate-x-2]="showRemainingView"
                [class.translate-x-0]="!showRemainingView"
              >
                <!-- Icon inside toggle -->
                <div class="w-full h-full flex items-center justify-center">
                  <svg
                    *ngIf="showRemainingView"
                    class="w-2 h-2 text-green-600"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clip-rule="evenodd"
                    />
                  </svg>
                  <svg
                    *ngIf="!showRemainingView"
                    class="w-2 h-2 text-blue-600"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
            </button>
          </div>

          <!-- Progress Bar -->
          <div class="w-full bg-gray-200 rounded-full h-2 mb-2">
            <div
              class="h-2 rounded-full transition-all duration-300"
              [style.background-color]="getUsageBarColor()"
              [style.width.%]="getUsageBarWidth()"
            ></div>
          </div>

          <div class="flex justify-between text-xs text-gray-500">
            <span>{{ getImagesText() }}</span>
            <span
              class="font-medium"
              [class.text-red-600]="getImagePercentage() >= 90"
              [class.text-amber-600]="
                getImagePercentage() >= 75 && getImagePercentage() < 90
              "
              [class.text-green-600]="getImagePercentage() < 75"
            >
              {{
                getImagePercentage() >= 90
                  ? (translations.nearLimit || "Near Limit")
                  : getImagePercentage() >= 75
                  ? (translations.warning || "Warning")
                  : (translations.good || "Good")
              }}
            </span>
          </div>
        </div>

        <!-- Detailed Stats -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div class="bg-blue-50 rounded-lg p-4">
            <div class="flex items-center mb-2">
              <svg
                class="w-5 h-5 text-blue-600 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                ></path>
              </svg>
              <h4 class="font-medium text-blue-900">{{ translations.imagesUsed || 'Images Used' }}</h4>
            </div>
            <p class="text-2xl font-bold text-blue-700">
              {{ (currentStats.imagesUsed || 0).toLocaleString() }}
            </p>
            <p class="text-sm text-blue-600">
              {{ translations.of || 'of' }} {{ (currentStats.imagesLimit || 0).toLocaleString() }}
            </p>
          </div>

          <div class="bg-green-50 rounded-lg p-4">
            <div class="flex items-center mb-2">
              <svg
                class="w-5 h-5 text-green-600 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                ></path>
              </svg>
              <h4 class="font-medium text-green-900">{{ translations.remaining || 'Remaining' }}</h4>
            </div>
            <p class="text-2xl font-bold text-green-700">
              {{
                (
                  currentStats.imagesLimit - currentStats.imagesUsed
                ).toLocaleString()
              }}
            </p>
            <p class="text-sm text-green-600">{{ translations.imagesAvailable || 'images available' }}</p>
          </div>
        </div>

        <!-- Additional Stats -->
        <div class="mt-4 bg-gray-50 rounded-lg p-4">
          <h4 class="text-sm font-medium text-gray-700 mb-2">{{ translations.statistics || 'Statistics' }}</h4>
          <div class="grid grid-cols-1 gap-2 text-sm">
            <div class="flex justify-between">
              <span class="text-gray-600">{{ translations.totalImagesProcessed || 'Total Images Processed:' }}</span>
              <span class="font-medium text-gray-900">
                {{ (currentStats.totalImagesProcessed || 0).toLocaleString() }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-600">{{ translations.lastActive || 'Last Active:' }}</span>
              <span class="font-medium text-gray-900">
                {{
                  currentStats.lastActiveAt
                    ? formatDate(currentStats.lastActiveAt)
                    : (translations.never || "Never")
                }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-600">{{ translations.accountCreated || 'Account Created:' }}</span>
              <span class="font-medium text-gray-900">
                {{ formatDate(currentStats.lastResetDate) }}
              </span>
            </div>
          </div>
        </div>

        <!-- Warning Message -->
        <div
          *ngIf="currentStats.isOverLimit"
          class="mt-4 bg-red-50 border border-red-200 rounded-lg p-4"
        >
          <div class="flex items-start">
            <svg
              class="w-5 h-5 text-red-600 mr-2 flex-shrink-0 mt-0.5"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fill-rule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clip-rule="evenodd"
              />
            </svg>
            <div>
              <h4 class="text-sm font-medium text-red-900 mb-1">
                {{ translations.imageLimitReached || 'Image Limit Reached' }}
              </h4>
              <p class="text-sm text-red-700">
                {{ translations.imageLimitReachedMessage || 'You have reached your image processing limit. Purchase more images to continue.' }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div *ngIf="!currentStats" class="text-center py-8">
        <div
          class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"
        ></div>
        <p class="mt-2 text-sm text-gray-500">{{ translations.loadingImageUsageData || 'Loading image usage data...' }}</p>
      </div>
    </div>
  </div>
</div>
